import React, { useState } from 'react';
import { ArrowLeft, Check } from 'lucide-react';
import { createProfile, profileNameExists } from '../../utils/profileManager';
import { avatars, profileColors } from '../../data/avatars';
import { playSound } from '../../utils/soundManager';
import AvatarPicker from './AvatarPicker';

const ProfileCreator = ({ onProfileCreated, onCancel }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    avatar: '👤',
    color: '#3498db'
  });
  const [errors, setErrors] = useState({});
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);

  const validateStep1 = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Please enter a name';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    } else if (formData.name.trim().length > 20) {
      newErrors.name = 'Name must be less than 20 characters';
    } else if (profileNameExists(formData.name)) {
      newErrors.name = 'This name is already taken';
    }

    if (!formData.age) {
      newErrors.age = 'Please select an age';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep1()) {
      playSound('click');
      setStep(2);
    } else {
      playSound('error');
    }
  };

  const handleBack = () => {
    playSound('click');
    if (step === 1) {
      onCancel();
    } else {
      setStep(step - 1);
    }
  };

  const handleSubmit = () => {
    playSound('success');
    const newProfile = createProfile(formData);
    onProfileCreated(newProfile);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleAvatarSelect = (avatar) => {
    handleInputChange('avatar', avatar.emoji);
    setShowAvatarPicker(false);
    playSound('click');
  };

  const handleColorSelect = (color) => {
    handleInputChange('color', color.color);
    playSound('click');
  };

  if (showAvatarPicker) {
    return (
      <AvatarPicker
        selectedAvatar={formData.avatar}
        onSelect={handleAvatarSelect}
        onBack={() => setShowAvatarPicker(false)}
      />
    );
  }

  return (
    <div className="profile-creator-container">
      <div className="profile-creator-content animate-fadeIn">
        <div className="profile-creator-header">
          <button
            className="back-button hover-scale btn-press"
            onClick={handleBack}
          >
            <ArrowLeft size={24} />
          </button>
          <h2>Create New Profile</h2>
          <div className="step-indicator">
            Step {step} of 2
          </div>
        </div>

        <div className="profile-creator-body">
          {step === 1 && (
            <div className="creator-step animate-slideInRight">
              <div className="form-section">
                <h3>👋 What's your name?</h3>
                <input
                  type="text"
                  className={`profile-input ${errors.name ? 'input-error' : ''}`}
                  placeholder="Enter your name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  maxLength={20}
                  autoFocus
                />
                {errors.name && (
                  <p className="error-message animate-shake">{errors.name}</p>
                )}
              </div>

              <div className="form-section">
                <h3>🎂 How old are you?</h3>
                <div className="age-selector">
                  {[...Array(13)].map((_, i) => {
                    const age = i + 3; // Ages 3-15
                    return (
                      <button
                        key={age}
                        className={`age-button hover-scale btn-press ${
                          formData.age === age ? 'selected' : ''
                        }`}
                        onClick={() => handleInputChange('age', age)}
                      >
                        {age}
                      </button>
                    );
                  })}
                </div>
                {errors.age && (
                  <p className="error-message animate-shake">{errors.age}</p>
                )}
              </div>

              <button
                className="next-button hover-lift btn-press"
                onClick={handleNext}
              >
                Next
                <Check size={20} />
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="creator-step animate-slideInRight">
              <div className="form-section">
                <h3>🎨 Choose your avatar</h3>
                <div className="avatar-preview-container">
                  <div
                    className="avatar-preview hover-scale"
                    style={{ background: formData.color }}
                    onClick={() => setShowAvatarPicker(true)}
                  >
                    <span className="avatar-preview-emoji">{formData.avatar}</span>
                  </div>
                  <button
                    className="change-avatar-button hover-lift btn-press"
                    onClick={() => setShowAvatarPicker(true)}
                  >
                    Change Avatar
                  </button>
                </div>
              </div>

              <div className="form-section">
                <h3>🌈 Pick your favorite color</h3>
                <div className="color-selector">
                  {profileColors.map((color) => (
                    <button
                      key={color.id}
                      className={`color-button hover-scale ${
                        formData.color === color.color ? 'selected' : ''
                      }`}
                      style={{ background: color.color }}
                      onClick={() => handleColorSelect(color)}
                      title={color.name}
                    >
                      {formData.color === color.color && (
                        <Check size={20} color="white" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <div className="profile-preview">
                <h3>Preview</h3>
                <div className="preview-card">
                  <div
                    className="preview-avatar"
                    style={{ background: formData.color }}
                  >
                    <span>{formData.avatar}</span>
                  </div>
                  <div className="preview-info">
                    <h4>{formData.name}</h4>
                    <p>Age {formData.age}</p>
                  </div>
                </div>
              </div>

              <button
                className="create-button hover-lift btn-press"
                onClick={handleSubmit}
              >
                <Check size={20} />
                Create Profile
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileCreator;
